import { useRef, useState } from 'react';
import { Rep } from '../../model/workout.domain';
import { button, input, wrapper } from './RepInput.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { TextInput } from '@client/new/pages/promotions/view/components/TextInput';
import { defaults } from '../../view-model/workout.schema';

export type RepInputProps = {
  value: Rep;
  onChange: (e: { target: { value: Rep } }) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
};

export function RepsInput({
  value,
  onChange,
  onBlur,
  placeholder = 'Enter reps',
  disabled,
}: RepInputProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');

  const wrappedOnChange = (newValue: Rep) => {
    onChange({ target: { value: newValue } });
  };

  const switchToFixed = () => {
    wrappedOnChange({ type: 'fixed', value: defaults.reps.default });
    setTimeout(() => ref.current?.focus(), 50);
  };

  const switchToAMRAP = () => {
    wrappedOnChange({ type: 'AMRAP' });
  };

  const onInputFocus = (_e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (value.type === 'AMRAP') return;
    setInputValue(String(value.value ?? ''));
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    const num = parseInt(e.target.value, 10);
    if (isNaN(num) || num === 0) return switchToAMRAP();
    wrappedOnChange({
      type: 'fixed',
      value: Math.max(Math.min(num, defaults.reps.max), defaults.reps.min),
    });
    setInputValue(String(num));
    onBlur?.(e);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  if (value.type === 'AMRAP') {
    return (
      <div className={wrapper}>
        <span>AMRAP</span>
        <button
          type="button"
          className={button}
          onClick={switchToFixed}
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
    );
  }

  return (
    <TextInput
      className={input}
      data-testid="rep-input"
      ref={ref}
      type="number"
      disabled={disabled}
      value={isFocused ? inputValue : String(value.value ?? '')}
      placeholder={placeholder}
      onBlur={onInputBlur}
      onFocus={onInputFocus}
      max={defaults.reps.max}
      onChange={onInputChange}
    />
  );
}
